import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Badge, Dropdown } from 'antd';
import {
  CaretUpFilled,
  ExclamationCircleOutlined,
  SoundFilled
} from '@ant-design/icons';
import { Spin } from 'antd';
import { formatTimeDifference } from '../../../../util/timezone';
import {
  NotificationMenuWrapper,
  StyledBellIcon,
  StyledMenu,
  StyledMenuItem,
  TriggerButtonContainer,
  BellIconWrapper,
  CaretContainer,
  NotificationHeader,
  NotificationTimestamp,
  NotificationContent,
  NotificationStatusMessage,
  StatusIcon
} from './styled';
import { TRANSACTION_HISTORY_MODULES as MODULES } from '../../../constants/commonConstants';
import {
  navigateWithRefresh,
  parseNotificationAdditionalProps
} from './notificationNavigation';

const NotificationsDropdown = ({
  history,
  getAMLSCommonNotifications,
  notifications = [],
  notificationsApiStatus = false,
  unreadCount = 0,
  moduleId
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [hasNewNotifications, setHasNewNotifications] = useState(true);

  const handleMenuClick = (e) => {
    setDropdownVisible(true);
  };

  useEffect(() => {
    if (dropdownVisible) getAMLSCommonNotifications();
  }, [dropdownVisible]);

  const triggerButton = (
    <TriggerButtonContainer
      onClick={() => setDropdownVisible(!dropdownVisible)}>
      <BellIconWrapper>
        {notifications?.length ? (
          <Badge
            dot={hasNewNotifications}
            offset={[-12, 4]}
            style={{
              backgroundColor: hasNewNotifications ? '#FF7B7B' : 'transparent',
              boxShadow: 'none'
            }}>
            <StyledBellIcon />
          </Badge>
        ) : (
          <StyledBellIcon />
        )}
      </BellIconWrapper>
      {dropdownVisible && (
        <CaretContainer id="topbar-id">
          <CaretUpFilled
            style={{
              fontSize: '20px',
              color: 'white',
              marginTop: '2px'
            }}
          />
        </CaretContainer>
      )}
    </TriggerButtonContainer>
  );

  const handleClickNotification = (notification) => {
    setDropdownVisible(false);

    // Check for scan completed notification (special case)
    if (notification.text.toLowerCase().includes('scan completed')) {
      const additionalProps = parseNotificationAdditionalProps(notification);
      history.push({
        pathname: `/app/smart-screening/on-demand-screening`,
        search: '?tabId=4',
        state: {
          jobId: additionalProps?.jobId
        }
      });
      return;
    }

    // Parse and extract fields from additional properties
    const additionalProps = parseNotificationAdditionalProps(notification);
    const { module, downloadType, alertId, partyId, caseId, customerId } =
      additionalProps;

    // Check if this is a transaction history notification
    const isHistoryNotif = module?.toLowerCase()?.includes('_history');

    if (isHistoryNotif) {
      // Case-type download (e.g., case transaction history)
      if (downloadType === 'case') {
        navigateWithRefresh({
          history,
          targetPath: `/app/cm/cases-by-department/${caseId}/take-action`,
          search: '?downloadHistory=true'
        });
        return;
      }

      // CM case transaction history
      if (module === MODULES.CM_CASE_TRANSACTION_HISTORY && caseId) {
        navigateWithRefresh({
          history,
          targetPath: `/app/cm/cases-by-department/${caseId}`,
          search: '?downloadHistory=true'
        });
        return;
      }

      // CM case-level TM/CRS transaction history — navigate to case page with alert tab + download history
      if (
        (module === MODULES.CM_CASE_TM_TRANSACTION_HISTORY ||
          module === MODULES.CM_CASE_CRS_TRANSACTION_HISTORY) &&
        caseId
      ) {
        navigateWithRefresh({
          history,
          targetPath: `/app/cm/cases-by-department/${caseId}`,
          search: `?alertId=${alertId}&tab=downloadHistory`
        });
        return;
      }

      // Alert-based downloads
      if (alertId) {
        // CM alert transaction history (TM/CRS)
        if (
          module === MODULES.CM_TM_TRANSACTION_HISTORY ||
          module === MODULES.CM_CRS_TRANSACTION_HISTORY
        ) {
          navigateWithRefresh({
            history,
            targetPath: `/app/cm/alerts/${alertId}`,
            search: '?tab=downloadHistory'
          });
          return;
        }

        // TRM transaction history
        if (module === MODULES.TM_TRANSACTION_HISTORY) {
          navigateWithRefresh({
            history,
            targetPath: `/app/trm/run-instance/${alertId}`,
            search: '?tab=downloadHistory'
          });
          return;
        }

        // CRS transaction history
        if (module === MODULES.CRS_TRANSACTION_HISTORY) {
          navigateWithRefresh({
            history,
            targetPath: `/app/crs/details/${partyId || customerId}`,
            search: '?tab=downloadHistory'
          });
          return;
        }

        // CM manual customer/case transaction history
        if (
          module === MODULES.CM_MANUAL_CUSTOMER_TRANSACTION_HISTORY ||
          module === MODULES.CM_MANUAL_CASE_TRANSACTION_HISTORY
        ) {
          navigateWithRefresh({
            history,
            targetPath: `/app/cm/cases-by-department/${caseId}`,
            search: `?customerId=${partyId || customerId}&tab=downloadHistory`
          });
          return;
        }
      } else {
        // PartyId-based CRS (no alertId)
        if (partyId && module === MODULES.CRS_TRANSACTION_HISTORY) {
          navigateWithRefresh({
            history,
            targetPath: `/app/crs/details/${partyId || customerId}`,
            search: '?tab=downloadHistory'
          });
          return;
        }
      }
    }
  };

  return (
    <Dropdown
      id="topbar-dropdown"
      overlay={
        <NotificationMenuWrapper>
          <StyledMenu onClick={handleMenuClick}>
            <StyledMenuItem
              key="1"
              style={{
                height: '48px',
                borderBottom: '1px solid #f7f7f8',
                margin: '0px'
              }}>
              <NotificationHeader>
                <span>Notifications</span>
                {notificationsApiStatus ? (
                  <Spin size="small" />
                ) : (
                  <StatusIcon>
                    <SoundFilled />
                  </StatusIcon>
                )}
              </NotificationHeader>
            </StyledMenuItem>
            {notifications && notifications?.length ? (
              notifications?.map((i, index) => {
                return (
                  <StyledMenuItem
                    key={index + 2}
                    onClick={() => handleClickNotification(i)}
                    style={{
                      height: 'auto',
                      minHeight: '56px',
                      borderBottom: '1px solid #f7f7f8',
                      margin: '0px',
                      padding: '8px 12px',
                      cursor: 'pointer'
                    }}>
                    <NotificationContent>
                      <span className="bullet-text">&#8226;</span>
                      <span className="notification-text">{i.text}</span>
                    </NotificationContent>
                    <NotificationTimestamp>
                      {formatTimeDifference(i.createdAt)}
                    </NotificationTimestamp>
                  </StyledMenuItem>
                );
              })
            ) : (
              <>
                {!notificationsApiStatus && (
                  <StyledMenuItem
                    key="2"
                    style={{
                      height: '56px',
                      borderBottom: '1px solid #f7f7f8',
                      justifyContent: 'space-around',
                      margin: '0px'
                    }}>
                    <NotificationStatusMessage>
                      No records found
                      <ExclamationCircleOutlined />
                    </NotificationStatusMessage>
                  </StyledMenuItem>
                )}
              </>
            )}
          </StyledMenu>
        </NotificationMenuWrapper>
      }
      trigger={['click']}
      visible={dropdownVisible}
      onVisibleChange={(visible) => setDropdownVisible(visible)}
      placement="bottomLeft">
      {triggerButton}
    </Dropdown>
  );
};

NotificationsDropdown.propTypes = {
  getAMLSCommonNotifications: PropTypes.func,
  notifications: PropTypes.array,
  notificationsApiStatus: PropTypes.bool,
  unreadCount: PropTypes.number,
  history: PropTypes.object
};

export default NotificationsDropdown;
